/** Staff ID, bank details and documents. Shapes copied from `workers/api/src/staff/documents/routes.ts`. */
import type { ApiClient } from "./client";

export interface StaffDetails {
  pan: string | null;
  aadhaarLast4: string | null;
  dob: string | null;
  address: string | null;
  emergencyContact: { name: string; phone: string | null; relation: string | null } | null;
  /** The full account number is never here: only the reveal route returns it. */
  bank: { holderName: string; accountMasked: string | null; accountLast4: string | null; ifsc: string | null; bankName: string | null } | null;
  version: number;
  updatedAt: number;
}

export interface DetailsPatch {
  pan?: string | null;
  /** All 12 digits; the server keeps the last 4 and drops the rest. */
  aadhaar?: string | null;
  dob?: string | null;
  address?: string | null;
  emergencyContact?: { name: string; phone: string; relation: string } | null;
  bank?: { holderName: string; accountNumber?: string; ifsc: string; bankName?: string | null } | null;
  version?: number;
}

export const DOCUMENT_KINDS = ["pan_card", "id_proof", "cancelled_cheque", "offer_letter", "other"] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];
export const DOCUMENT_LABEL: Record<DocumentKind, string> = {
  pan_card: "PAN card",
  id_proof: "ID proof",
  cancelled_cheque: "Cancelled cheque",
  offer_letter: "Offer letter",
  other: "Other",
};

export interface StaffDocument { id: string; kind: DocumentKind; filename: string; contentType: string; bytes: number; uploadedByStaffId: string; createdAt: number }

export const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"] as const;

export const getDetails = (c: ApiClient, staffId: string) => c.get<{ details: StaffDetails | null }>(`/staff/${staffId}/details`);
export const saveDetails = (c: ApiClient, staffId: string, body: DetailsPatch) => c.patch<{ details: StaffDetails | null; changed: string[] }>(`/staff/${staffId}/details`, body);
/** Owner only today (staff.bank_view_full). Every call is audited on the server. */
export const revealAccount = (c: ApiClient, staffId: string) => c.post<{ accountNumber: string }>(`/staff/${staffId}/bank-account/reveal`);

export const listDocuments = (c: ApiClient, staffId: string) => c.get<{ documents: StaffDocument[]; maxBytes: number }>(`/staff/${staffId}/documents`);
export const uploadDocument = (c: ApiClient, staffId: string, kind: DocumentKind, file: Blob, filename: string) =>
  c.request<{ document: StaffDocument }>("POST", `/staff/${staffId}/documents`, { query: { kind, filename }, body: file });
export const deleteDocument = (c: ApiClient, staffId: string, docId: string) => c.del<{ deleted: boolean }>(`/staff/${staffId}/documents/${docId}`);
/** Owner only today (staff.documents_download). Returns a 60-second, single-use link; audited on the server. */
export const documentLink = (c: ApiClient, staffId: string, docId: string) => c.post<{ url: string; expiresAt: number }>(`/staff/${staffId}/documents/${docId}/link`);

/** Format checks that mirror the server's, so most mistakes are caught before a round trip. The server still decides. */
export const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
export const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const digitsOf = (s: string) => s.replace(/[\s-]/g, "");

export function fileProblem(file: { size: number; type: string }): string | null {
  if (file.size === 0) return "The file is empty";
  if (file.size > MAX_DOCUMENT_BYTES) return "A document is at most 5 MB";
  if (file.type && !(ACCEPTED_TYPES as readonly string[]).includes(file.type)) return "Only PDF, JPEG, PNG and WebP files are accepted";
  return null;
}

export const formatBytes = (n: number) => (n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
